import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import { ArrowRight, Crown, EnvelopeSimple, Handshake, BookOpen } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import Countdown from "@/components/Countdown";

function FreePreviewCapture() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email, source: "free_preview" });
      toast.success("Preview unlocked — taking you to issue #001");
      setUnlocked(true);
      setEmail("");
      // Brief delay so the user sees the success, then auto-redirect to the comic reader
      setTimeout(() => navigate("/comics"), 1100);
    } catch {
      toast.error("Could not unlock. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="free-preview-section" className="relative border-t border-white/10 py-20 lg:py-24 overflow-hidden" data-testid="free-preview-section">
      <div className="absolute inset-0 pointer-events-none opacity-25">
        <img
          src="/api/static/images/comic-001-cover.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover blur-3xl scale-125"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
        {/* Cover */}
        <div className="lg:col-span-5 flex justify-center order-2 lg:order-1">
          <div className="relative group">
            <div className="absolute -inset-3 bg-gradient-to-r from-[#FF3B30] via-[#FFD700] to-[#FF3B30] opacity-25 blur-2xl group-hover:opacity-40 transition-opacity" />
            <img
              src="/api/static/images/comic-001-cover.jpg"
              alt="The Savage King: Legacy #001 cover"
              className="relative w-[260px] sm:w-[320px] h-auto shadow-2xl shadow-red-900/40 border border-white/10"
              data-testid="free-preview-cover"
            />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="section-eyebrow">
            <BookOpen size={14} weight="fill" /> Free Preview · 3 Pages
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase mt-3 leading-[0.95]" data-testid="free-preview-heading">
            Read The First <span className="text-[#FF3B30]">3 Pages</span> Of Legacy <span className="text-[#FFD700]">#001</span>. Free.
          </h2>
          <p className="text-white/75 mt-5 max-w-lg">
            Drop your email and we&apos;ll unlock a 3-page preview of <span className="text-white">The Savage King: Legacy</span> instantly. No spam. Just the comic.
          </p>

          {!unlocked ? (
            <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="free-preview-form">
              <label htmlFor="free-preview-email" className="sr-only">Email</label>
              <div className="relative flex-1">
                <EnvelopeSimple size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-dim" />
                <input
                  id="free-preview-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-black/70 border border-white/15 focus:border-[#FF3B30] focus:outline-none pl-12 pr-4 py-4 text-base"
                  data-testid="free-preview-email-input"
                  disabled={submitting}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-red inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="free-preview-submit"
              >
                {submitting ? "Unlocking..." : "Get My 3 Pages"} <ArrowRight size={18} weight="bold" />
              </button>
            </form>
          ) : (
            <div className="mt-7 scu-card p-5 border-[#FFD700]/40 max-w-xl" data-testid="free-preview-success">
              <div className="font-display uppercase tracking-widest text-[#FFD700] text-sm">Preview Unlocked</div>
              <div className="text-white/80 mt-1 text-sm">Loading Issue #001...</div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-dim uppercase tracking-widest">
            <span>✓ Instant Access</span>
            <span>✓ No Spam</span>
            <span>✓ Unsubscribe Anytime</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [comics, setComics] = useState([]);
  const [merch, setMerch] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/comics").then((r) => setComics(r.data)).catch(() => {});
    api.get("/merch").then((r) => setMerch(r.data)).catch(() => {});
  }, []);

  const featured = comics.find((c) => c.featured) || comics[0];

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="/api/static/images/hero-savage-king.jpg"
            alt="The Savage King mid-air in the ring"
            className="w-full h-full object-cover opacity-70"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl animate-stamp">
            <div className="section-eyebrow" data-testid="hero-eyebrow">Issue #001 · October 2026</div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.92] uppercase mt-6" data-testid="hero-title" aria-label="The Savage King Legacy">
              The Savage<br />King Legacy
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/85 max-w-xl">
              Welcome to the SCU. A comic universe forged from the squared circle and the streets that raised it. Pre-order the first issue and step inside.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
              <Link to="/comics" className="btn-red inline-flex items-center gap-2" data-testid="hero-cta-preorder">
                Pre-Order Legacy #001 <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/about" className="btn-ghost inline-flex items-center gap-2" data-testid="hero-cta-about">
                Meet The King
              </Link>
            </div>
          </div>
        </div>

        {/* Kinetic side text */}
        <div className="absolute right-0 bottom-0 w-full">
          <Marquee speed={30} gradient={false} className="font-display text-[120px] sm:text-[160px] leading-none uppercase tracking-tighter text-white/[0.04] select-none">
            <span className="mx-8">LONG LIVE THE KING</span>
            <span className="mx-8">★</span>
            <span className="mx-8">SAVAGE</span>
            <span className="mx-8">★</span>
          </Marquee>
        </div>
      </section>

      {/* Pre-launch Countdown */}
      <Countdown />

      {/* BRAND SHOWCASE — The official SCU emblem */}
      <section className="relative overflow-hidden bg-black border-y border-white/10 py-20 lg:py-28" data-testid="brand-showcase">
        <div className="absolute inset-0 opacity-30">
          <img
            src="/api/static/images/brand-logo.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover blur-2xl scale-125"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="section-eyebrow justify-center">The Emblem</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3">
            Welcome To The <span className="text-[#FFD700]">Savage Comix Universe</span>
          </h2>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto">
            A universe where flawed warriors rise, the squared circle meets the cosmic battleground, and every hero is forged in fire.
          </p>
          <div className="mt-12 flex justify-center">
            <div className="relative inline-block group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FF3B30] via-[#FFD700] to-[#FF3B30] opacity-30 blur-2xl group-hover:opacity-50 transition-opacity" />
              <img
                src="/api/static/images/brand-logo.png"
                alt="Savage Comix Universe official emblem"
                className="relative max-w-full w-[640px] lg:w-[760px] h-auto shadow-2xl shadow-[#FF3B30]/20"
                data-testid="brand-logo-art"
              />
            </div>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/about" className="btn-red" data-testid="brand-cta-about">Discover The Universe</Link>
            <Link to="/comics" className="btn-ghost" data-testid="brand-cta-comics">Read The Comic</Link>
          </div>
        </div>
      </section>

      <FreePreviewCapture />

      {/* Featured Comic - Bento */}
      {featured && (
        <section className="max-w-7xl mx-auto px-6 py-24" data-testid="featured-comic-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 scu-card overflow-hidden">
              <img src={featured.cover_image} alt={featured.title} className="w-full h-[520px] object-cover" />
            </div>
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="section-eyebrow">Flagship Title</div>
              <h2 className="font-display uppercase text-5xl lg:text-6xl mt-4 leading-[0.95]">{featured.title}</h2>
              <div className="font-display tracking-widest text-[#FF3B30] text-xl mt-2">{featured.issue}</div>
              <p className="mt-6 text-white/80 leading-relaxed">{featured.synopsis}</p>
              <div className="mt-6 flex items-center gap-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-dim">Price</div>
                  <div className="font-display text-2xl">${featured.price?.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-dim">Release</div>
                  <div className="font-display text-2xl">{featured.release_date}</div>
                </div>
              </div>
              <div className="mt-8 flex gap-3">
                <Link to="/comics" className="btn-red" data-testid="featured-cta-shop">Shop Issues</Link>
                <Link to="/shop" className="btn-ghost" data-testid="featured-cta-merch">Merch</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Merch teaser */}
      <section className="bg-[#0F0F0F] border-y border-white/10 py-24" data-testid="merch-teaser-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <div className="section-eyebrow">Drop One</div>
              <h2 className="font-display text-5xl lg:text-6xl uppercase mt-3">Wear The Crown</h2>
            </div>
            <Link to="/shop" className="btn-ghost inline-flex items-center gap-2" data-testid="merch-view-all">
              View All <ArrowRight size={18} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {merch.slice(0, 4).map((m) => (
              <Link to="/shop" key={m.id} className="scu-card group block" data-testid={`merch-card-${m.id}`}>
                <div className="aspect-[4/5] overflow-hidden bg-black">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="font-display text-lg uppercase tracking-wider">{m.name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-dim text-xs uppercase tracking-widest">{m.category}</span>
                    <span className="font-display text-xl text-[#FF3B30]">${m.price?.toFixed(2)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsors — Founding Partner Program */}
      <section className="relative border-t border-white/10 py-24 overflow-hidden" data-testid="sponsors-section">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 18px, #FF3B30 18px, #FF3B30 19px)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="section-eyebrow justify-center">
              <Handshake size={14} weight="fill" /> Founding Sponsors
            </div>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-3 leading-[0.95]">
              Stand With The <span className="text-[#FFD700]">King</span>.
            </h2>
            <p className="mt-5 text-white/75">
              SCU is in pre-launch. Now is the moment to attach your brand to a debut comic universe with a built-in wrestling audience.
              Founding sponsor spots are open for the first wave of partners.
            </p>
          </div>

          {/* Empty sponsor slots — placeholders until partners are signed */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map((n) => (
              <div
                key={n}
                className="aspect-[4/3] border border-dashed border-white/15 hover:border-[#FFD700] transition-colors flex flex-col items-center justify-center text-center px-4 group cursor-default"
                data-testid={`sponsor-slot-${n}`}
              >
                <Crown size={28} weight="duotone" className="text-white/30 group-hover:text-[#FFD700] transition-colors" />
                <div className="mt-3 font-display text-xs tracking-[0.3em] text-white/40 group-hover:text-[#FFD700] uppercase transition-colors">
                  Your Brand Here
                </div>
                <div className="mt-1 text-[10px] text-dim uppercase tracking-wider">Slot 0{n} · Open</div>
              </div>
            ))}
          </div>

          {/* Benefits */}
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {[
              { k: "01", t: "Logo Placement", v: "Featured on this page, in the comic, and across SCU socials." },
              { k: "02", t: "First-Issue Listing", v: "Sponsor credit in Issue #001 — permanent in the print run." },
              { k: "03", t: "Co-Branded Drops", v: "Limited edition co-branded merch and signed editions." },
            ].map((b) => (
              <div key={b.k} className="scu-card p-6" data-testid={`sponsor-benefit-${b.k}`}>
                <div className="font-display text-3xl text-[#FFD700]">{b.k}</div>
                <div className="mt-2 font-display text-xl uppercase">{b.t}</div>
                <p className="text-dim text-sm mt-2 leading-relaxed">{b.v}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="mailto:Info@savagecomixuniverse.com?subject=SCU%20Sponsorship%20Inquiry&body=I%E2%80%99m%20interested%20in%20becoming%20a%20founding%20sponsor%20of%20the%20Savage%20ComiX%20Universe.%20Please%20send%20me%20the%20sponsorship%20deck."
              className="btn-red inline-flex items-center gap-2"
              data-testid="sponsor-cta-email"
            >
              <Handshake size={18} weight="bold" /> Become A Sponsor
            </a>
            <div className="mt-4 text-xs text-dim uppercase tracking-widest">
              Inquiries · <a href="mailto:Info@savagecomixuniverse.com" className="text-white/70 hover:text-[#FF3B30] transition-colors">Info@savagecomixuniverse.com</a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="newsletter-section">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="section-eyebrow">Join The Court</div>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-3 leading-[0.95]">
              Get The Drop<br />Before Anyone Else.
            </h2>
            <p className="mt-4 text-white/75 max-w-lg">
              Issue releases, merch drops, live events, signed editions — straight to your inbox. No spam, just savagery.
            </p>
          </div>
          <div className="lg:col-span-5">
            <form onSubmit={subscribe} className="scu-card p-8 flex flex-col gap-4" data-testid="newsletter-form">
              <label className="text-xs uppercase tracking-widest text-dim flex items-center gap-2">
                <EnvelopeSimple size={14} /> Email Address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@savage.com"
                className="bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none"
                data-testid="newsletter-email-input"
              />
              <button type="submit" disabled={loading} className="btn-red" data-testid="newsletter-submit-btn">
                {loading ? "Joining..." : "Enlist Me"}
              </button>
              <div className="text-[11px] text-dim flex items-center gap-2"><Crown size={12} weight="fill" /> All hail the crown.</div>
            </form>
          </div>
        </div>
      </section>

      {/* Stats / value */}
      <section className="border-t border-white/10 py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: "1", v: "Flagship Title" },
            { k: "∞", v: "Stories Coming" },
            { k: "100%", v: "Built In The Ring" },
            { k: "0", v: "Apologies" },
          ].map((s) => (
            <div key={s.v} className="text-center" data-testid={`stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-display text-6xl text-[#FF3B30]">{s.k}</div>
              <div className="text-xs uppercase tracking-widest text-dim mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
